/**
 * API Transformation Functions
 *
 * This file contains functions that transform data between frontend
 * and backend formats. The backend expects a nested structure while
 * the frontend uses a flat structure.
 */

import type { MasterFlightRecord } from './models';

/**
 * Transforms flat MasterFlightRecord to nested API format required by backend
 *
 * The backend expects data grouped into logical sections:
 * - basicInfo: Flight identification
 * - schedule: Dates and frequency
 * - metadata: File tracking info
 * - stations: Route information (cleaned of MongoDB fields)
 *
 * Note: Removes MongoDB-generated fields (_id, createdAt, updatedAt) from payload
 * The _id should be in the URL path, not the request body
 *
 * @param record - Frontend MasterFlightRecord (flat structure)
 * @returns API payload (nested structure, cleaned for updates)
 */
export function transformToApiFormat(record: MasterFlightRecord): any {
  // Clean stations array - remove MongoDB fields AND codeshare data
  // Codeshares are managed separately via the codeshare API
  const cleanStations = record.stations.map((station) => {
    const { _id, createdAt, updatedAt, codeshareFlightNo, ...cleanStation } = station as any;

    // Build station object with ONLY camelCase fields (no PascalCase duplicates)
    return {
      // Required fields
      departureStation: cleanStation.departureStation,
      arrivalStation: cleanStation.arrivalStation,
      std: cleanStation.std,
      sta: cleanStation.sta,
      iataSubTypeCode: cleanStation.iataSubTypeCode,
      aircraftConfiguration: cleanStation.aircraftConfiguration,

      // Optional fields - send as null if empty
      passengerTerminalDep: cleanStation.passengerTerminalDep || null,
      passengerTerminalArr: cleanStation.passengerTerminalArr || null,
      utcLocalTimeVariationDep: cleanStation.utcLocalTimeVariationDep || null,
      utcLocalTimeVariationArr: cleanStation.utcLocalTimeVariationArr || null,
      cd: cleanStation.cd !== undefined && cleanStation.cd !== null ? cleanStation.cd : null,
      ca: cleanStation.ca !== undefined && cleanStation.ca !== null ? cleanStation.ca : null,
      aircraftOwner: cleanStation.aircraftOwner || null,
      tailNo: cleanStation.tailNo || null,
      onwardFlight: cleanStation.onwardFlight || null,
    };
  });

  // Clean additionalFields - remove _id and empty strings
  const cleanAdditionalFields = record.additionalFields
    ? (() => {
        const { _id, ...rest } = record.additionalFields as any;
        // Filter out empty string values
        return Object.fromEntries(
          Object.entries(rest).filter(([_, value]) => value !== '')
        );
      })()
    : undefined;

  return {
    // Group 1: Basic flight info
    basicInfo: {
      flightOwner: record.flightOwner,
      flightNo: record.flightNo,
      seasonId: record.seasonId,
      itineraryVarId: record.itineraryVarId,
      operationalSuffix: record.operationalSuffix || null,
      scheduleStatus: record.scheduleStatus,
    },

    // Group 2: Schedule
    schedule: {
      startDate: record.startDate,
      endDate: record.endDate,
      frequency: record.frequency,
      freqRate: record.freqRate || '', // Empty string for weekly (required by backend)
      iataServiceTypeCode: record.iataServiceTypeCode,
    },

    // Group 3: File metadata
    metadata: {
      creationDate: record.creationDate || new Date().toISOString(),
      fileName: record.fileName || null,
      bucketPath: record.bucketPath || null,
      messageType: record.messageType || null,
      isAdhoc: record.isAdhoc || false,
      parentFlightId: record.parentFlightId || null,
    },

    // Stations array (cleaned of MongoDB fields and codeshare data)
    stations: cleanStations,

    // NOTE: codeshareFlightNo is NOT included here
    // Codeshares are managed separately via the codeshare API (see useCodeshareSyncManager)

    // Optional: Backend-managed fields (send as null if empty)
    // NOTE: _id and csfsKey are NOT included - backend manages these
    airlineUserRef: record.airlineUserRef || null,
    trackingSsm: record.trackingSsm || null,
    trackingAsm: record.trackingAsm || null,
    mesgSeqRef: record.mesgSeqRef || null,
    creatorRef: record.creatorRef || null,
    actionId: record.actionId || null,

    // Optional: Additional fields (cleaned of _id and empty strings)
    // Only include if there are non-empty fields
    ...(cleanAdditionalFields && Object.keys(cleanAdditionalFields).length > 0 && {
      additionalFields: cleanAdditionalFields
    }),
  };
}
